import type { Block, Variable } from './types'

export function generateId(): string {
  return 'b' + Math.random().toString(36).slice(2, 9)
}

export const BLOCK_DEFAULTS: Record<string, () => Partial<Block>> = {
  text: () => ({ content: 'Enter your text here...' }),
  chart: () => ({
    config: {
      categories: ['OCT', 'NOV', 'DEC'],
      baselines: [75, 60, 85],
    },
  }),
  button: () => ({
    label: 'Click me',
    config: { variant: 'primary' },
  }),
  image: () => ({ config: { url: '' } }),
  steps: () => ({ config: { steps: ['Step 1', 'Step 2', 'Step 3'], stepGroups: {} } }),
  chat_bubble: () => ({
    config: {
      messages: [
        { text: 'Hello!', sender: 'left', name: 'User' },
        { text: 'Hi there!', sender: 'right', name: 'Bot' },
      ],
    },
  }),
  codeblock: () => ({
    config: { code: 'console.log("Hello world")', language: 'javascript' },
  }),
  toggle: () => ({ label: 'Toggle', config: { onLabel: 'ON', offLabel: 'OFF' } }),
  dropdown: () => ({
    label: 'Select an option',
    config: {
      options: [
        { label: 'Option 1', value: 'option_1' },
        { label: 'Option 2', value: 'option_2' },
        { label: 'Option 3', value: 'option_3' },
      ],
    },
  }),
  tabs: () => ({
    config: {
      tabs: [
        { label: 'Tab 1', value: 'tab_1', content: 'Content for tab 1' },
        { label: 'Tab 2', value: 'tab_2', content: 'Content for tab 2' },
        { label: 'Tab 3', value: 'tab_3', content: 'Content for tab 3' },
      ],
    },
  }),
  divider: () => ({ config: { style: 'solid', height: 32, color: '#e2e8f0' } }),
  video: () => ({ config: { url: '', aspectRatio: '16/9' } }),
  number_input: () => ({ label: 'Number', config: { placeholder: '0', suffix: '' } }),
  poll: () => ({
    label: 'What do you think?',
    config: {
      options: [
        { label: 'Option A', value: 'option_a' },
        { label: 'Option B', value: 'option_b' },
        { label: 'Option C', value: 'option_c' },
      ],
      showResults: 'after_vote',
      style: 'buttons',
    },
  }),
  quiz: () => ({
    label: 'What is the capital of France?',
    config: {
      options: [
        { label: 'London', value: 'london' },
        { label: 'Paris', value: 'paris' },
        { label: 'Berlin', value: 'berlin' },
      ],
      correctValue: 'paris',
      explanation: 'Paris is the capital of France since the 10th century.',
      showExplanation: true,
      allowRetry: true,
    },
  }),
}

export interface BlockTemplate {
  name: string
  description: string
  create: (variables: Variable[]) => { blocks: Block[]; variables: Variable[] }
}

export const TEMPLATES: BlockTemplate[] = [
  {
    name: 'Pricing Demo',
    description: 'Slider + chart + dynamic text',
    create: (existingVars) => {
      const varName = `price_ratio`
      const sliderId = generateId()
      const textId = generateId()
      const chartId = generateId()
      return {
        variables: [
          ...existingVars,
          { name: varName, type: 'slider' as const, default: 50, min: 0, max: 100, step: 5 },
        ],
        blocks: [
          { id: sliderId, type: 'slider' as const, variable: varName, label: 'Price Ratio' },
          { id: textId, type: 'text' as const, content: `Current ratio is @${varName}% — adjust the slider to see projected impact.` },
          { id: chartId, type: 'chart' as const, source: varName, label: 'Revenue Projection', config: { categories: ['Q1', 'Q2', 'Q3', 'Q4'], baselines: [60, 70, 65, 80] } },
        ],
      }
    },
  },
  {
    name: 'FAQ Guide',
    description: 'Step-by-step walkthrough',
    create: () => ({
      variables: [],
      blocks: [
        { id: generateId(), type: 'text' as const, content: 'Follow these steps to get started:' },
        { id: generateId(), type: 'steps' as const, config: { steps: ['Create your account', 'Choose a template or start from scratch', 'Add interactive blocks', 'Publish and share!'], stepGroups: {} } },
      ],
    }),
  },
  {
    name: 'Conversation',
    description: 'Chat bubble exchange',
    create: () => ({
      variables: [],
      blocks: [
        {
          id: generateId(),
          type: 'chat_bubble' as const,
          config: {
            messages: [
              { text: 'Hey, how does this work?', sender: 'left', name: 'Alex' },
              { text: 'Just drag blocks and connect them with @variables!', sender: 'right', name: 'Bot' },
              { text: 'That easy? Let me try!', sender: 'left', name: 'Alex' },
            ],
          },
        },
      ],
    }),
  },
]
